/**
 * "Report a missing call" dialog (spec §7.4, Task 14): a call the caller
 * expected but cannot find in VoiceIQ at all, so there is no interaction to
 * attach the report to. Collects an approximate date/time, direction, the
 * number if known, and up to three separate "expected" hints (person, queue
 * or auto attendant, site or branch) so the resulting email prints each as
 * its own line rather than one ambiguous field.
 */
import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Input,
  MessageBar,
  MessageBarBody,
  Radio,
  RadioGroup,
  Text,
  Textarea,
  makeStyles,
  tokens,
  type RadioGroupOnChangeData,
} from '@fluentui/react-components';
import { api } from '../../services/api';
import type { SupportReportSummary } from '../../types';
import { strings } from '../../strings/en';
import { NativeDateInput } from '../common/NativeDateInput';
import { useTimezone } from '../common/TimezoneProvider';

const useStyles = makeStyles({
  content: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalM,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalXS,
  },
  dateTimeRow: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
  },
});

type Direction = 'inbound' | 'outbound' | 'unknown';

export interface ReportMissingCallDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmitted: (report: SupportReportSummary) => void;
}

type Phase = 'form' | 'submitting' | 'result';

export function ReportMissingCallDialog({ open, onClose, onSubmitted }: ReportMissingCallDialogProps) {
  const styles = useStyles();
  const { timezone } = useTimezone();
  const [idempotencyKey, setIdempotencyKey] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [direction, setDirection] = useState<Direction>('unknown');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [description, setDescription] = useState('');
  const [expectedUser, setExpectedUser] = useState('');
  const [expectedQueue, setExpectedQueue] = useState('');
  const [expectedBranch, setExpectedBranch] = useState('');
  const [phase, setPhase] = useState<Phase>('form');
  const [result, setResult] = useState<SupportReportSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setIdempotencyKey(crypto.randomUUID());
      setDate('');
      setTime('');
      setDirection('unknown');
      setPhoneNumber('');
      setDescription('');
      setExpectedUser('');
      setExpectedQueue('');
      setExpectedBranch('');
      setPhase('form');
      setResult(null);
      setError(null);
    } else {
      setIdempotencyKey('');
    }
  }, [open]);

  async function submit() {
    setPhase('submitting');
    setError(null);
    try {
      const report = await api.createMissingCallReport({
        idempotency_key: idempotencyKey,
        description,
        approximate_time: date ? `${date}T${time || '00:00'}:00` : undefined,
        timezone: date ? timezone : undefined,
        direction,
        phone_number: phoneNumber.trim() || undefined,
        expected_user: expectedUser.trim() || undefined,
        expected_queue: expectedQueue.trim() || undefined,
        expected_branch: expectedBranch.trim() || undefined,
      });
      setResult(report);
      setPhase('result');
      onSubmitted(report);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to send report.');
      setPhase('form');
    }
  }

  const submitting = phase === 'submitting';

  return (
    <Dialog
      open={open}
      onOpenChange={(_event, data) => {
        if (!data.open) onClose();
      }}
    >
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{strings.report.missingTitle}</DialogTitle>
          <DialogContent className={styles.content}>
            {error ? (
              <MessageBar intent="error">
                <MessageBarBody>{error}</MessageBarBody>
              </MessageBar>
            ) : null}

            {phase === 'result' && result ? (
              <MessageBar intent={result.delivery?.succeeded === false ? 'warning' : 'success'}>
                <MessageBarBody>
                  {result.delivery?.succeeded === false
                    ? strings.report.deliveryFailed(result.reference)
                    : strings.report.missingSubmitted(result.reference)}
                </MessageBarBody>
              </MessageBar>
            ) : (
              <>
                <Text>{strings.report.missingIntro}</Text>
                <div className={styles.field}>
                  <label id="missing-call-when-label">{strings.report.missingWhen}</label>
                  <div className={styles.dateTimeRow}>
                    <NativeDateInput value={date} onChange={setDate} />
                    <Input
                      type="time"
                      value={time}
                      onChange={(_event, data) => setTime(data.value)}
                      disabled={submitting}
                    />
                  </div>
                </div>
                <div className={styles.field}>
                  <label id="missing-call-direction-label">{strings.report.missingDirection}</label>
                  <RadioGroup
                    aria-labelledby="missing-call-direction-label"
                    value={direction}
                    onChange={(_event, data: RadioGroupOnChangeData) => setDirection(data.value as Direction)}
                    disabled={submitting}
                  >
                    <Radio value="inbound" label={strings.report.directionInbound} />
                    <Radio value="outbound" label={strings.report.directionOutbound} />
                    <Radio value="unknown" label={strings.report.directionUnknown} />
                  </RadioGroup>
                </div>
                <div className={styles.field}>
                  <label htmlFor="missing-call-number">{strings.report.missingNumber}</label>
                  <Input
                    id="missing-call-number"
                    value={phoneNumber}
                    onChange={(_event, data) => setPhoneNumber(data.value)}
                    disabled={submitting}
                  />
                </div>
                <div className={styles.field}>
                  <label htmlFor="missing-call-expected-user">{strings.report.missingExpectedUser}</label>
                  <Input
                    id="missing-call-expected-user"
                    value={expectedUser}
                    onChange={(_event, data) => setExpectedUser(data.value)}
                    disabled={submitting}
                  />
                </div>
                <div className={styles.field}>
                  <label htmlFor="missing-call-expected-queue">{strings.report.missingExpectedQueue}</label>
                  <Input
                    id="missing-call-expected-queue"
                    value={expectedQueue}
                    onChange={(_event, data) => setExpectedQueue(data.value)}
                    disabled={submitting}
                  />
                </div>
                <div className={styles.field}>
                  <label htmlFor="missing-call-expected-branch">{strings.report.missingExpectedBranch}</label>
                  <Input
                    id="missing-call-expected-branch"
                    value={expectedBranch}
                    onChange={(_event, data) => setExpectedBranch(data.value)}
                    disabled={submitting}
                  />
                </div>
                <div className={styles.field}>
                  <label htmlFor="missing-call-description">{strings.report.description}</label>
                  <Textarea
                    id="missing-call-description"
                    value={description}
                    onChange={(_event, data) => setDescription(data.value)}
                    placeholder={strings.report.descriptionPlaceholder}
                    disabled={submitting}
                    resize="vertical"
                  />
                </div>
              </>
            )}
          </DialogContent>
          <DialogActions>
            {phase === 'result' && result ? (
              <Button appearance="primary" onClick={onClose}>
                {strings.report.close}
              </Button>
            ) : (
              <>
                <Button appearance="secondary" onClick={onClose} disabled={submitting}>
                  {strings.report.cancel}
                </Button>
                <Button
                  appearance="primary"
                  onClick={submit}
                  disabled={submitting || description.trim().length < 3}
                >
                  {strings.report.submit}
                </Button>
              </>
            )}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
